/**
 * LayoutTab - Section ordering and visibility management for the landing page
 * Supports drag-and-drop (desktop) and up/down arrows (all devices)
 */
import { useState, useRef } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Switch } from '../ui/switch';
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from '../ui/select';
import { getDefaultSectionBg } from '../landing/sectionChrome';
import {
  ChevronUp, ChevronDown, GripVertical, Eye, EyeOff,
  LayoutGrid, Sparkles, Palette, Play, MessageSquare, Phone,
  CreditCard, HelpCircle, Megaphone, Monitor, Navigation, Image as ImageIcon,
  Plus, Trash2, Mic
} from 'lucide-react';

const SECTION_ICONS = {
  dashboard_preview: Monitor,
  features: Sparkles,
  voice_features: Mic,
  hero2: Sparkles,
  customization: Palette,
  video: Play,
  gallery: ImageIcon,
  testimonials: MessageSquare,
  pricing: CreditCard,
  faq: HelpCircle,
  primary_cta: Megaphone,
  secondary_cta: Megaphone,
  contact: Phone,
  final_cta: Megaphone,
};

const DEFAULT_ORDER = [
  { id: 'dashboard_preview', label: 'Your Team Dashboard Awaits', visible: true },
  { id: 'voice_features', label: 'Voice Features (Built for Coaches in Motion)', visible: true },
  { id: 'hero2', label: 'Hero2 · Secondary Hero (Stack & Stamp checklist)', visible: false },
  { id: 'features', label: 'Features', visible: true },
  { id: 'customization', label: 'Make It Yours', visible: true },
  { id: 'video', label: 'Product Demo / See It In Action', visible: true },
  { id: 'gallery', label: 'Image Gallery', visible: true },
  { id: 'testimonials', label: 'Testimonials', visible: true },
  { id: 'pricing', label: 'Pricing', visible: true },
  { id: 'faq', label: 'FAQs', visible: true },
  { id: 'primary_cta', label: 'Primary CTA', visible: true },
  { id: 'secondary_cta', label: 'Secondary CTA', visible: false },
  { id: 'contact', label: 'Contact', visible: true },
  { id: 'final_cta', label: 'Final CTA', visible: true },
];

export function LayoutTab({ content, setContent }) {
  // Merge saved order with defaults so new sections appear at their DEFAULT_ORDER index
  // (spliced after the nearest known previous default neighbour) instead of being appended.
  const saved = content?.section_order || [];
  // Lookup table so we can fill in missing labels/icons on saved entries
  // that were persisted before defaults included full metadata.
  const defaultsById = Object.fromEntries(DEFAULT_ORDER.map(d => [d.id, d]));
  const savedIds = new Set(saved.map(s => s.id));
  let merged;
  if (saved.length === 0) {
    merged = DEFAULT_ORDER;
  } else {
    // Hydrate each saved entry with the default label so the rendered list
    // never shows blank rows (only icon + arrows) for older saves.
    merged = saved.map(s => ({
      ...defaultsById[s.id],
      ...s,
      label: s.label || defaultsById[s.id]?.label || s.id,
    }));
    DEFAULT_ORDER.forEach((def, defIdx) => {
      if (savedIds.has(def.id)) return;
      let insertAfterId = null;
      for (let k = defIdx - 1; k >= 0; k -= 1) {
        if (savedIds.has(DEFAULT_ORDER[k].id)) {
          insertAfterId = DEFAULT_ORDER[k].id;
          break;
        }
      }
      const targetIdx = insertAfterId
        ? merged.findIndex(s => s.id === insertAfterId) + 1
        : 0;
      merged.splice(targetIdx, 0, def);
      savedIds.add(def.id);
    });
  }
  const sections = merged.length > 0 ? merged : DEFAULT_ORDER;
  const [dragIndex, setDragIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const dragItemRef = useRef(null);

  const updateOrder = (newOrder) => {
    setContent(prev => ({ ...prev, section_order: newOrder }));
  };

  const moveSection = (fromIndex, toIndex) => {
    if (toIndex < 0 || toIndex >= sections.length) return;
    const updated = [...sections];
    const [moved] = updated.splice(fromIndex, 1);
    updated.splice(toIndex, 0, moved);
    updateOrder(updated);
  };

  const toggleVisibility = (index) => {
    const updated = sections.map((s, i) =>
      i === index ? { ...s, visible: !s.visible } : s
    );
    updateOrder(updated);
  };

  const updateSectionBg = (sectionId, field, value) => {
    setContent(prev => ({
      ...prev,
      section_styles: {
        ...(prev.section_styles || {}),
        [sectionId]: {
          ...(prev.section_styles?.[sectionId] || {}),
          [field]: value,
        },
      },
    }));
  };

  const resetSectionBg = (sectionId) => {
    setContent(prev => {
      const next = { ...(prev.section_styles || {}) };
      delete next[sectionId];
      return { ...prev, section_styles: next };
    });
  };

  // Drag handlers
  const handleDragStart = (e, index) => {
    setDragIndex(index);
    dragItemRef.current = index;
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', index.toString());
    // Make the drag ghost slightly transparent
    requestAnimationFrame(() => {
      e.target.style.opacity = '0.5';
    });
  };

  const handleDragEnd = (e) => {
    e.target.style.opacity = '1';
    setDragIndex(null);
    setDragOverIndex(null);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (index !== dragOverIndex) {
      setDragOverIndex(index);
    }
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    const fromIndex = dragItemRef.current;
    if (fromIndex !== null && fromIndex !== dropIndex) {
      moveSection(fromIndex, dropIndex);
    }
    setDragIndex(null);
    setDragOverIndex(null);
  };

  // Touch drag handlers
  const touchStartY = useRef(null);
  const touchIndex = useRef(null);

  const handleTouchStart = (index, e) => {
    touchStartY.current = e.touches[0].clientY;
    touchIndex.current = index;
  };

  const handleTouchEnd = (e) => {
    if (touchStartY.current === null || touchIndex.current === null) return;
    const deltaY = e.changedTouches[0].clientY - touchStartY.current;
    const threshold = 40; // px
    if (Math.abs(deltaY) > threshold) {
      const direction = deltaY > 0 ? 1 : -1;
      moveSection(touchIndex.current, touchIndex.current + direction);
    }
    touchStartY.current = null;
    touchIndex.current = null;
  };

  const visibleCount = sections.filter(s => s.visible).length;

  return (
    <div className="space-y-6" data-testid="layout-tab">
      <div>
        <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
          <LayoutGrid className="w-5 h-5 text-emerald-600" />
          Page Layout
        </h2>
        <p className="text-sm text-gray-500 mt-1">
          Drag sections or use arrows to reorder. Toggle visibility to show or hide sections.
          Header, hero and footer stay in fixed positions.
        </p>
      </div>

      {/* Fixed: Header + Hero */}
      <div className="space-y-1.5">
        <div className="flex items-center gap-3 px-4 py-3 bg-gray-100 rounded-lg" data-testid="header-row">
          <div className="w-5 h-5" />
          <div className="flex-1">
            <p className="text-sm font-medium text-gray-700">Header & Navigation</p>
            <p className="text-[11px] text-gray-500 mt-0.5">Toggle the "Get Started" CTA button shown in the menu header</p>
          </div>
          <div className="flex items-center gap-2" data-testid="nav-cta-visibility">
            <span className="text-[10px] text-gray-500 font-medium uppercase tracking-wide hidden sm:inline">CTA Button</span>
            {content?.branding?.nav_cta_visible === false ? (
              <EyeOff className="w-3.5 h-3.5 text-gray-400" />
            ) : (
              <Eye className="w-3.5 h-3.5 text-emerald-600" />
            )}
            <Switch
              checked={content?.branding?.nav_cta_visible !== false}
              onCheckedChange={(checked) => setContent(prev => ({
                ...prev,
                branding: { ...(prev.branding || {}), nav_cta_visible: checked }
              }))}
              data-testid="nav-cta-toggle"
            />
          </div>
          <span className="text-[10px] text-gray-400 font-medium uppercase">Fixed</span>
        </div>
        <div className="flex items-center gap-3 px-4 py-3 bg-gray-100 rounded-lg opacity-60">
          <div className="w-5 h-5" />
          <div className="flex-1">
            <p className="text-sm font-medium text-gray-500">Hero Section</p>
          </div>
          <span className="text-[10px] text-gray-400 font-medium uppercase">Fixed</span>
        </div>
      </div>

      {/* Reorderable sections */}
      <div className="space-y-1.5" data-testid="sortable-sections">
        {sections.map((section, index) => {
          // Custom CTAs use dynamic ids like `custom_cta_<hash>` and won't be
          // in SECTION_ICONS — fall back to the Megaphone icon so they read
          // consistently with the built-in CTA sections.
          const isCustomCta = typeof section.id === 'string' && section.id.startsWith('custom_cta_');
          const Icon = SECTION_ICONS[section.id] || (isCustomCta ? Megaphone : LayoutGrid);
          const isDragging = dragIndex === index;
          const isDragOver = dragOverIndex === index && dragIndex !== index;
          const styles = content?.section_styles?.[section.id] || {};
          const defaultLight = getDefaultSectionBg(section.id, 'light');
          const defaultDark = getDefaultSectionBg(section.id, 'dark');
          const bgLight = styles.bgColor || defaultLight;
          const bgDark = styles.bgColorDark || defaultDark;
          const hasCustomBg = !!(styles.bgColor || styles.bgColorDark);

          return (
            <div
              key={section.id}
              className={`
                rounded-lg border transition-all select-none
                ${isDragging ? 'opacity-50 border-dashed border-emerald-400 bg-emerald-50' : ''}
                ${isDragOver ? 'border-emerald-500 bg-emerald-50 shadow-sm' : 'border-gray-200 bg-white'}
                ${!section.visible ? 'opacity-60' : ''}
              `}
              data-testid={`section-row-${section.id}`}
            >
              <div
                draggable
                onDragStart={(e) => handleDragStart(e, index)}
                onDragEnd={handleDragEnd}
                onDragOver={(e) => handleDragOver(e, index)}
                onDrop={(e) => handleDrop(e, index)}
                onTouchStart={(e) => handleTouchStart(index, e)}
                onTouchEnd={handleTouchEnd}
                className="flex items-center gap-2 sm:gap-3 px-3 sm:px-4 py-3"
              >
                {/* Drag handle */}
                <div className="cursor-grab active:cursor-grabbing touch-none text-gray-400 hover:text-gray-600">
                  <GripVertical className="w-4 h-4 sm:w-5 sm:h-5" />
                </div>

                {/* Position number */}
                <span className="text-xs font-mono text-gray-400 w-4 text-center shrink-0">{index + 1}</span>

                {/* Icon + Label */}
                <Icon className={`w-4 h-4 shrink-0 ${section.visible ? 'text-emerald-600' : 'text-gray-400'}`} />
                <span className={`flex-1 text-sm font-medium truncate ${section.visible ? 'text-gray-900' : 'text-gray-400 line-through'}`}>
                  {section.label}
                </span>

                {/* Up/Down arrows */}
                <div className="flex items-center gap-0.5 shrink-0">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => moveSection(index, index - 1)}
                    disabled={index === 0}
                    className="h-7 w-7 p-0"
                    data-testid={`move-up-${section.id}`}
                  >
                    <ChevronUp className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => moveSection(index, index + 1)}
                    disabled={index === sections.length - 1}
                    className="h-7 w-7 p-0"
                    data-testid={`move-down-${section.id}`}
                  >
                    <ChevronDown className="w-4 h-4" />
                  </Button>
                </div>

                {/* Visibility toggle */}
                <div className="flex items-center gap-1.5 shrink-0">
                  {section.visible ? (
                    <Eye className="w-3.5 h-3.5 text-emerald-600" />
                  ) : (
                    <EyeOff className="w-3.5 h-3.5 text-gray-400" />
                  )}
                  <Switch
                    checked={section.visible}
                    onCheckedChange={() => toggleVisibility(index)}
                    className="scale-75"
                    data-testid={`toggle-${section.id}`}
                  />
                </div>
              </div>

              {/* Background colours (per section) */}
              <div className="flex items-center gap-3 px-3 sm:px-4 pb-3 pt-0 flex-wrap text-xs text-gray-500">
                <span>Background:</span>
                <label className="inline-flex items-center gap-1.5">
                  <span>Light</span>
                  <input
                    type="color"
                    value={bgLight}
                    onChange={(e) => updateSectionBg(section.id, 'bgColor', e.target.value)}
                    className="h-6 w-8 rounded border border-gray-200 cursor-pointer"
                    data-testid={`section-bg-light-${section.id}`}
                    aria-label={`${section.label} light background`}
                  />
                </label>
                <label className="inline-flex items-center gap-1.5">
                  <span>Dark</span>
                  <input
                    type="color"
                    value={bgDark}
                    onChange={(e) => updateSectionBg(section.id, 'bgColorDark', e.target.value)}
                    className="h-6 w-8 rounded border border-gray-200 cursor-pointer"
                    data-testid={`section-bg-dark-${section.id}`}
                    aria-label={`${section.label} dark background`}
                  />
                </label>
                {hasCustomBg && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => resetSectionBg(section.id)}
                    className="h-6 px-2 text-[11px] text-gray-400 hover:text-gray-600"
                    data-testid={`section-bg-reset-${section.id}`}
                  >
                    Reset
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Fixed: Footer */}
      <div className="space-y-1.5">
        <div className="flex items-center gap-3 px-4 py-3 bg-gray-100 rounded-lg opacity-60">
          <div className="w-5 h-5" />
          <div className="flex-1">
            <p className="text-sm font-medium text-gray-500">Footer</p>
          </div>
          <span className="text-[10px] text-gray-400 font-medium uppercase">Fixed</span>
        </div>
      </div>

      {/* Summary */}
      <p className="text-xs text-gray-500 text-center">
        {visibleCount} of {sections.length} sections visible. Changes apply after saving.
      </p>

      {/* Navigation Menu Order & Labels */}
      <NavMenuSection content={content} setContent={setContent} />
    </div>
  );
}

// ─── Navigation Menu Ordering Sub-component ───

const DEFAULT_NAV_ORDER = [
  { id: 'features', label: 'Features', target: 'features', visible: true },
  { id: 'customization', label: 'Make It Yours', target: 'customization', visible: true },
  { id: 'demo', label: 'Product Demo', target: 'video', visible: true },
  { id: 'pricing', label: 'Pricing', target: 'pricing', visible: true },
  { id: 'testimonials', label: 'Testimonials', target: 'testimonials', visible: true },
  { id: 'faq', label: 'FAQ', target: 'faq', visible: true },
  { id: 'contact', label: 'Contact', target: 'contact', visible: true },
];

const TARGET_SECTIONS = [
  { value: 'dashboard_preview', label: 'Dashboard Preview' },
  { value: 'hero2', label: 'Hero2 (Stack & Stamp)' },
  { value: 'features', label: 'Features' },
  { value: 'customization', label: 'Make It Yours' },
  { value: 'video', label: 'Product Demo / Video' },
  { value: 'voice_features', label: 'Voice Features' },
  { value: 'gallery', label: 'Image Gallery' },
  { value: 'testimonials', label: 'Testimonials' },
  { value: 'pricing', label: 'Pricing' },
  { value: 'faq', label: 'FAQ' },
  { value: 'primary_cta', label: 'Primary CTA' },
  { value: 'secondary_cta', label: 'Secondary CTA' },
  { value: 'contact', label: 'Contact' },
  { value: 'final_cta', label: 'Final CTA' },
];

const TARGET_PAGES = [
  { value: '/', label: 'Home' },
  { value: '/about', label: 'About Page' },
  { value: '/privacy-policy', label: 'Privacy Policy' },
  { value: '/login', label: 'Sign In' },
  { value: '/onboarding', label: 'Sign Up / Onboarding' },
];

const CUSTOM_URL_VALUE = '__custom_url__';

function NavMenuSection({ content, setContent }) {
  const saved = content?.branding?.nav_order || [];
  const savedIds = new Set(saved.map(s => s.id));
  const merged = saved.length > 0 ? [...saved, ...DEFAULT_NAV_ORDER.filter(d => !savedIds.has(d.id))] : DEFAULT_NAV_ORDER;
  const navItems = merged.map(item => ({
    ...item,
    visible: item.visible !== false,
    target: item.target || item.id,
  }));
  const navLabels = content?.branding?.nav_labels || {};
  const [dragIdx, setDragIdx] = useState(null);
  const [overIdx, setOverIdx] = useState(null);
  const dragRef = useRef(null);

  const updateNavOrder = (newOrder) => {
    setContent(prev => ({
      ...prev,
      branding: { ...prev.branding, nav_order: newOrder }
    }));
  };

  const updateLabel = (key, value) => {
    setContent(prev => ({
      ...prev,
      branding: {
        ...prev.branding,
        nav_labels: { ...(prev.branding?.nav_labels || {}), [key]: value }
      }
    }));
  };

  const updateItemField = (index, field, value) => {
    const updated = navItems.map((item, i) => (i === index ? { ...item, [field]: value } : item));
    updateNavOrder(updated);
  };

  const toggleNavVisibility = (index) => {
    updateItemField(index, 'visible', !navItems[index].visible);
  };

  const moveItem = (from, to) => {
    if (to < 0 || to >= navItems.length) return;
    const updated = [...navItems];
    const [moved] = updated.splice(from, 1);
    updated.splice(to, 0, moved);
    updateNavOrder(updated);
  };

  const addCustomItem = () => {
    const id = `custom_${Date.now()}`;
    const newItem = { id, label: 'New Menu Item', target: 'features', visible: true, custom: true };
    updateNavOrder([...navItems, newItem]);
  };

  const removeItem = (index) => {
    const updated = navItems.filter((_, i) => i !== index);
    updateNavOrder(updated);
  };

  const onDragStart = (e, i) => {
    setDragIdx(i);
    dragRef.current = i;
    e.dataTransfer.effectAllowed = 'move';
    requestAnimationFrame(() => { e.target.style.opacity = '0.5'; });
  };
  const onDragEnd = (e) => { e.target.style.opacity = '1'; setDragIdx(null); setOverIdx(null); };
  const onDragOver = (e, i) => { e.preventDefault(); if (i !== overIdx) setOverIdx(i); };
  const onDrop = (e, dropI) => {
    e.preventDefault();
    const from = dragRef.current;
    if (from !== null && from !== dropI) moveItem(from, dropI);
    setDragIdx(null); setOverIdx(null);
  };

  // Determine which select value to show — predefined target or "Custom URL"
  const targetSelectValue = (target) => {
    const allValues = [...TARGET_SECTIONS, ...TARGET_PAGES].map(o => o.value);
    if (target && allValues.includes(target)) return target;
    return CUSTOM_URL_VALUE;
  };

  const onTargetChange = (index, value) => {
    if (value === CUSTOM_URL_VALUE) {
      // Clear target so user types a URL below
      updateItemField(index, 'target', '');
    } else {
      updateItemField(index, 'target', value);
    }
  };

  return (
    <div className="space-y-4 border-t pt-6" data-testid="nav-menu-section">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
            <Navigation className="w-5 h-5 text-emerald-600" />
            Navigation Menu
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Drag or use arrows to reorder. Edit labels and choose which section or page each link scrolls/navigates to.
          </p>
        </div>
        <Button onClick={addCustomItem} variant="outline" size="sm" data-testid="nav-add-item">
          <Plus className="w-4 h-4 mr-1.5" /> Add Menu Item
        </Button>
      </div>

      <div className="space-y-2">
        {navItems.map((item, index) => {
          const isDragging = dragIdx === index;
          const isDragOver = overIdx === index && dragIdx !== index;
          const selectVal = targetSelectValue(item.target);
          const showCustomUrl = selectVal === CUSTOM_URL_VALUE;
          return (
            <div
              key={item.id}
              draggable
              onDragStart={(e) => onDragStart(e, index)}
              onDragEnd={onDragEnd}
              onDragOver={(e) => onDragOver(e, index)}
              onDrop={(e) => onDrop(e, index)}
              className={`flex flex-col gap-2 px-4 py-3 rounded-lg border transition-all select-none ${
                isDragging ? 'opacity-50 border-dashed border-emerald-400 bg-emerald-50' :
                isDragOver ? 'border-emerald-500 bg-emerald-50 shadow-sm' :
                'border-gray-200 bg-white'
              } ${!item.visible ? 'opacity-60' : ''}`}
              data-testid={`nav-item-${item.id}`}
            >
              <div className="flex items-center gap-3">
                <div className="cursor-grab active:cursor-grabbing touch-none text-gray-400 hover:text-gray-600">
                  <GripVertical className="w-4 h-4" />
                </div>
                <span className="text-xs font-mono text-gray-400 w-4 text-center shrink-0">{index + 1}</span>
                <Input
                  value={navLabels[item.id] || item.label}
                  onChange={(e) => {
                    updateLabel(item.id, e.target.value);
                    if (item.custom) updateItemField(index, 'label', e.target.value);
                  }}
                  className={`flex-1 h-8 text-sm ${!item.visible ? 'opacity-50' : ''}`}
                  placeholder="Menu label"
                  data-testid={`nav-label-${item.id}`}
                />
                <div className="flex items-center gap-0.5 shrink-0">
                  <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => moveItem(index, index - 1)} disabled={index === 0} data-testid={`nav-up-${item.id}`}>
                    <ChevronUp className="w-4 h-4" />
                  </Button>
                  <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => moveItem(index, index + 1)} disabled={index === navItems.length - 1} data-testid={`nav-down-${item.id}`}>
                    <ChevronDown className="w-4 h-4" />
                  </Button>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  {item.visible ? (
                    <Eye className="w-3.5 h-3.5 text-emerald-600" />
                  ) : (
                    <EyeOff className="w-3.5 h-3.5 text-gray-400" />
                  )}
                  <Switch
                    checked={item.visible}
                    onCheckedChange={() => toggleNavVisibility(index)}
                    className="scale-75"
                    data-testid={`nav-toggle-${item.id}`}
                  />
                </div>
                {item.custom && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeItem(index)}
                    className="h-7 w-7 p-0 text-red-500 hover:text-red-600 shrink-0"
                    data-testid={`nav-remove-${item.id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                )}
              </div>

              <div className="flex items-center gap-2 pl-10 flex-wrap">
                <span className="text-xs text-gray-500 shrink-0">Goes to:</span>
                <Select value={selectVal} onValueChange={(v) => onTargetChange(index, v)}>
                  <SelectTrigger className="h-8 text-xs w-[220px]" data-testid={`nav-target-${item.id}`}>
                    <SelectValue placeholder="Choose a destination" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      <SelectLabel>Scroll to Section</SelectLabel>
                      {TARGET_SECTIONS.map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectGroup>
                    <SelectGroup>
                      <SelectLabel>Open Page</SelectLabel>
                      {TARGET_PAGES.map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectGroup>
                    <SelectGroup>
                      <SelectLabel>Other</SelectLabel>
                      <SelectItem value={CUSTOM_URL_VALUE}>Custom URL / Path</SelectItem>
                    </SelectGroup>
                  </SelectContent>
                </Select>
                {showCustomUrl && (
                  <Input
                    value={item.target || ''}
                    onChange={(e) => updateItemField(index, 'target', e.target.value)}
                    placeholder="https://example.com or /custom-path"
                    className="h-8 text-xs flex-1 min-w-[200px]"
                    data-testid={`nav-custom-url-${item.id}`}
                  />
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
